<template>
  <v-container fluid class="page-wrap fill-height">
    <v-row align="center" justify="center" class="fill-height">
      <v-col cols="12" sm="8" md="5" class="text-center">
        <v-card class="rounded-xl pa-6 pa-md-8" elevation="3">
            <v-avatar size="64" class="clock-icon mb-4" rounded>
                <v-icon color="white" size="32">mdi-map-marker-radius-outline</v-icon>
            </v-avatar>
            <div class="text-h6 font-weight-bold primary--text mb-1">Teacher Clock-In/Out</div>

            <div v-if="loading" class="text-body-2 grey--text mb-6">Loading...</div>

            <v-alert v-else-if="!teacher" type="warning" text border="left" class="text-left mt-4">
                Your account is not yet linked to a Staff record. Kindly ask your school admin to link your login under <strong>Attendance &gt; Manage Staff</strong>.
            </v-alert>

            <template v-else>
                <div class="text-body-2 grey--text mb-1">Hello {{ teacher.firstname }} {{ teacher.lastname }}</div>
                <div class="text-body-2 grey--text mb-6">Tap a button below to record your attendance from within an approved school zone</div>

                <v-card v-if="todayClockIn || todayClockOut" outlined class="rounded-lg pa-4 mb-4 text-left">
                    <div v-if="todayClockIn">
                        <v-icon left :color="todayClockIn.status === 'punctual' ? 'green' : 'orange'">
                            {{ todayClockIn.status === 'punctual' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                        Clocked in at {{ formatTime(todayClockIn.timestamp) }} ({{ todayClockIn.status }})
                    </div>
                    <div v-if="todayClockOut" class="mt-2">
                        <v-icon left color="grey">mdi-clock-out</v-icon>
                        Clocked out at {{ formatTime(todayClockOut.timestamp) }}
                    </div>
                </v-card>

                <v-btn color="primary" rounded elevation="2" x-large class="font-weight-bold text-none px-8"
                    @click="handleClock('in')" :disabled="!!todayClockIn || submitting" :loading="submitting">
                    <v-icon left>mdi-clock-in</v-icon> Clock In
                </v-btn>

                <div v-if="todayClockIn && !todayClockOut" class="mt-3">
                    <v-btn color="secondary" rounded elevation="2" x-large class="font-weight-bold text-none px-8"
                        @click="handleClock('out')" :disabled="submitting" :loading="submitting">
                        <v-icon left>mdi-clock-out</v-icon> Clock Out
                    </v-btn>
                </div>
            </template>

            <v-alert v-if="statusMessage" :type="statusSuccess ? 'success' : 'error'" text border="left" class="mt-6 text-left">
                {{ statusMessage }}
            </v-alert>

            <div v-if="locationAccuracy !== null" class="text-caption grey--text mt-3">
                Location Accuracy: {{ locationAccuracy }} meters
            </div>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import moment from 'moment';

export default {
  data() {
    return {
      schoolZones: [], // list of geofenced zones
      teacher: null,
      todayClockIn: null,
      todayClockOut: null,
      statusMessage: '',
      statusSuccess: false,
      locationAccuracy: null,
      loading: true,
      submitting: false,
      authToken: sessionStorage.getItem('authToken') ?? null,
      accessToken: sessionStorage.getItem('accessToken') ?? null,
      school: localStorage.getItem('school') ? JSON.parse(localStorage.getItem('school')) : {}
    };
  },
  mounted() {
    if (!this.authToken) {
        this.$router.push({name: 'Login'})
    }
    this.fetchClockStatus();
  },
  methods: {
    fetchClockStatus() {
      axios
        .get(`${process.env.VUE_APP_BASEURL}/attendances/clock-status?school_id=${this.school.school_id}`, {
          headers: {
            'Authorization': 'Bearer '+this.authToken,
            'X-Access-Token': this.accessToken,
            'ngrok-skip-browser-warning': 'true'
          }
        })
        .then(res => {
          this.teacher = res.data.teacher;
          this.schoolZones = (res.data.geofences || []).map(s => ({
            lat: parseFloat(s.lat),
            lng: parseFloat(s.lng),
            radius: parseFloat(s.radius),
            name: s.address ?? 'Unnamed zone'
          }));
          this.todayClockIn = res.data.today.clock_in;
          this.todayClockOut = res.data.today.clock_out;
        })
        .catch((error) => {
          this.$toast.error(error.response ? error.response.data.message : 'APP is temporarily unavailable. Wait or Contact us!', {
            timeout: 5000
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },

    handleClock(type) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const accuracy = position.coords.accuracy;
          this.locationAccuracy = Math.round(accuracy);

          if (accuracy > 500) {
            this.statusMessage = 'Your Location is not accurate enough. Use a Mobile phone with Location services enabled.';
            this.statusSuccess = false;
            return;
          }

          let withinZone = false;
          let closest = null;

          this.schoolZones.forEach(zone => {
            const distance = getDistanceFromLatLonInMeters(lat, lng, zone.lat, zone.lng);
            if (!closest || distance < closest.distance) {
              closest = { ...zone, distance };
            }
            if (distance <= zone.radius) {
              withinZone = true;
            }
          });

          if (!withinZone && closest) {
            this.statusMessage = `You are outside all approved school zones. Nearest is ${closest.name} (${Math.round(closest.distance)}m away)`;
            this.statusSuccess = false;
            return;
          }

          this.submitClock(type, lat, lng);
        },
        error => {
          this.statusMessage = 'Failed to retrieve location. Please enable location services.';
          this.statusSuccess = false;
          console.log(error)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    },

    submitClock(type, lat, lng) {
      this.submitting = true;
      axios.post(`${process.env.VUE_APP_BASEURL}/attendances/clock-${type}?school_id=${this.school.school_id}`, { lat, lng }, {
        headers: {
          'Authorization': 'Bearer '+this.authToken,
          'X-Access-Token': this.accessToken,
          'ngrok-skip-browser-warning': 'true'
        }
      })
      .then(res => {
        this.statusMessage = res.data.message;
        this.statusSuccess = true;
        this.fetchClockStatus();
      })
      .catch(error => {
        this.statusMessage = error.response ? error.response.data.message : 'APP is temporarily unavailable. Wait or Contact us!';
        this.statusSuccess = false;
      })
      .finally(() => {
        this.submitting = false;
      });
    },

    formatTime(timestamp) {
      return moment(timestamp).format('h:mm A');
    },
  }
};

// Utility function to calculate distance between two lat/lng points
function getDistanceFromLatLonInMeters(lat1, lon1, lat2, lon2) {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}
</script>

<style scoped>
.clock-icon {
    background: linear-gradient(135deg, #1E3C72 0%, #2A5298 100%);
}
</style>
